import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  getGiftCertificateTemplate,
  uploadGiftCertificateTemplate,
  giftCertificateTemplateUrl,
  GiftCertificateTemplate as TemplateInfo,
} from "../../api/api-hook";
import { getAdminSession } from "../../utils/adminAuth";

const BRAND_GREEN = "#023822";

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
};

const linkButton: React.CSSProperties = {
  backgroundColor: "#fff",
  color: BRAND_GREEN,
  border: `1px solid ${BRAND_GREEN}`,
  borderRadius: 8,
  padding: "0.3rem 0.85rem",
  fontSize: "0.8rem",
  fontWeight: 500,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
};

// The blank gift certificate PDF that the purchaser's name and gift card number are printed on.
// Admins can view it, preview it filled in, and upload a replacement.
const GiftCertificateTemplate = () => {
  const [info,      setInfo]      = useState<TemplateInfo | null>(null);
  const [pending,   setPending]   = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    getGiftCertificateTemplate()
      .then(setInfo)
      .catch(() => setError("Couldn't load the gift certificate PDF details."));
  }, []);

  const chooseFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    setSuccess(false);
    setError(null);
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please choose a PDF file.");
      return;
    }
    setPending(file);
  };

  const upload = async () => {
    if (!pending) return;
    setUploading(true);
    setError(null);
    try {
      setInfo(await uploadGiftCertificateTemplate(pending, getAdminSession()?.displayName || "Admin"));
      setPending(null);
      setSuccess(true);
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : null;
      setError(message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Version parameter so the links open the newest upload rather than a cached copy
  const version = info ? `v=${encodeURIComponent(info.updatedAt)}` : "";
  const viewUrl    = giftCertificateTemplateUrl() + (version ? `?${version}` : "");
  const previewUrl = giftCertificateTemplateUrl(true) + (version ? `&${version}` : "");

  return (
    <div style={{ background: "#fff", borderRadius: 12, padding: "0.9rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2" style={{ flex: "1 1 240px", minWidth: "min(100%, 240px)" }}>
          <i className="bi bi-file-earmark-pdf" style={{ fontSize: "1.4rem", color: "#c0392b" }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: BRAND_GREEN, fontSize: "0.9rem" }}>Gift Certificate PDF</div>
            <div className="text-muted" style={{ fontSize: "0.76rem", overflowWrap: "anywhere" }}>
              {info
                ? <>
                    {info.fileName} · {formatSize(info.size)} ·{" "}
                    {info.source === "uploaded"
                      ? `uploaded ${formatDate(info.updatedAt)}${info.updatedBy ? ` by ${info.updatedBy}` : ""}`
                      : "original version"}
                  </>
                : "Loading…"}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <a href={viewUrl} target="_blank" rel="noreferrer" style={linkButton}>
            <i className="bi bi-eye me-1" /> View
          </a>
          <a href={previewUrl} target="_blank" rel="noreferrer" style={linkButton} title="Opens the PDF with a sample name and gift card number filled in">
            <i className="bi bi-file-earmark-text me-1" /> Preview filled in
          </a>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            style={{ ...linkButton, backgroundColor: BRAND_GREEN, color: "#fff", border: "none" }}
          >
            <i className="bi bi-upload me-1" /> Upload new PDF
          </button>
          <input ref={fileInput} type="file" accept="application/pdf,.pdf" onChange={chooseFile} style={{ display: "none" }} />
        </div>
      </div>

      {pending && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3" style={{ background: "#fff8e6", border: "1px solid #f0e0b0", borderRadius: 8, padding: "0.55rem 0.85rem", fontSize: "0.8rem" }}>
          <span style={{ color: "#6b5512", overflowWrap: "anywhere" }}>
            Replace the current PDF with <strong>{pending.name}</strong>? Gift cards bought from now on will use it.
          </span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPending(null)} disabled={uploading}>
              Cancel
            </button>
            <button type="button" className="btn btn-sm" style={{ backgroundColor: BRAND_GREEN, color: "#fff" }} onClick={upload} disabled={uploading}>
              {uploading ? "Uploading…" : "Replace PDF"}
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="small mt-3" style={{ color: "#146c43" }}>
          <i className="bi bi-check-circle me-1" />
          New PDF saved. Use Preview filled in to check the name and gift card number line up with the boxes.
        </div>
      )}
      {error && <div className="small mt-3 text-danger">{error}</div>}
    </div>
  );
};

export default GiftCertificateTemplate;
